import React, { useState, useEffect, useRef } from "react";
import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage, auth } from "../../../db/firebase";
import { v4 as uuidv4 } from "uuid";
import { AnimatedModal, modalPanelClass } from "../../../components/AnimatedModal";
import { ChordChartWithHover } from "../../../components/ChordChartWithHover";
import { YoutubeVideosSection } from "../../../components/YoutubeVideosSection";
import { useTranslation } from "react-i18next";
import {
  HiOutlineXMark,
  HiOutlineMusicalNote,
  HiOutlinePaperClip,
  HiOutlinePencilSquare,
} from "react-icons/hi2";
import { transposeChords } from "../../../utils/transposeChords";
import {
  getYouTubeEmbedUrl,
  getYoutubeLinksFromSong,
  isYouTubeUrl,
} from "../../../utils/youtubeLinks";

interface Comment {
  id: string;
  userName: string;
  comment: string;
  timestamp: string;
  attachmentUrl?: string;
  attachmentType?: string;
  attachmentName?: string;
}

const MAX_FILE_SIZE_MB = 5;

const SongArtworkBlock: React.FC<{
  artworkURL?: string | null;
  title: string;
  t: (k: string) => string;
}> = ({ artworkURL, title, t }) => {
  const [imgError, setImgError] = useState(false);
  const showPlaceholder = !artworkURL || imgError;
  if (showPlaceholder) {
    return (
      <div
        className="flex items-center justify-center w-44 h-44 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 shrink-0"
        aria-hidden
      >
        <HiOutlineMusicalNote className="w-16 h-16" strokeWidth={1.5} />
      </div>
    );
  }
  return (
    <img
      src={artworkURL}
      alt={t("artwork")}
      onError={() => setImgError(true)}
      className="w-44 h-44 rounded-xl object-cover shrink-0 shadow-sm"
    />
  );
};

interface SongDetailModalProps {
  song: any;
  onClose: () => void;
  organizationId?: string | null;
}

const SongDetailModal: React.FC<SongDetailModalProps> = ({
  song: initialSong,
  onClose,
  organizationId: propOrgId,
}) => {
  const { t } = useTranslation();
  const [song, setSong] = useState<any>(initialSong);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentError, setAttachmentError] = useState("");
  const [chords, setChords] = useState("");
  const [savedChords, setSavedChords] = useState("");
  const [chordsEditing, setChordsEditing] = useState(false);
  const [chordsSaving, setChordsSaving] = useState(false);
  const [transposeSemitones, setTransposeSemitones] = useState(0);
  const [bpm, setBpm] = useState(120);
  const [isPlaying, setIsPlaying] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(propOrgId ?? null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleTranspose = (delta: number) => {
    const newVal = transposeSemitones + delta;
    if (newVal < -11 || newVal > 11) return;
    setTransposeSemitones(newVal);
  };

  const startChordsEdit = () => {
    if (transposeSemitones !== 0) {
      setChords(transposeChords(chords, transposeSemitones));
      setTransposeSemitones(0);
    }
    setChordsEditing(true);
  };

  const handleSaveChords = async () => {
    if (!song?.id) return;
    setChordsSaving(true);
    try {
      const updates: Record<string, unknown> = { chords };
      if (song.chordVersions?.length) {
        updates.chordVersions = song.chordVersions.map((v: { id: string; label: string; content: string }, i: number) =>
          i === 0 ? { ...v, content: chords } : v
        );
      }
      await updateDoc(doc(db, "songs", song.id), updates);
      setSong((prev: any) => (prev ? { ...prev, ...updates } : null));
      setSavedChords(chords);
      setChordsEditing(false);
      setTransposeSemitones(0);
    } catch (err) {
      console.error("Error saving chords:", err);
    } finally {
      setChordsSaving(false);
    }
  };

  const startMetronome = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
    }
    const playClick = () => {
      const osc = audioContextRef.current!.createOscillator();
      const gain = audioContextRef.current!.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(1000, audioContextRef.current!.currentTime);
      gain.gain.setValueAtTime(1, audioContextRef.current!.currentTime);
      osc.connect(gain);
      gain.connect(audioContextRef.current!.destination);
      osc.start();
      osc.stop(audioContextRef.current!.currentTime + 0.05);
    };
    playClick();
    timerRef.current = window.setInterval(playClick, (60 / bpm) * 1000);
  };

  const stopMetronome = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const toggleMetronome = () => {
    if (isPlaying) stopMetronome();
    else startMetronome();
    setIsPlaying(!isPlaying);
  };

  const handleAddComment = async () => {
    if ((newComment.trim() === "" && !attachment) || !song?.id) return;

    let attachmentUrl = "";
    let attachmentType = "";
    let attachmentName = "";

    if (attachment) {
      if (attachment.size / (1024 * 1024) > MAX_FILE_SIZE_MB) {
        setAttachmentError(t("fileSizeError", { maxFileSize: MAX_FILE_SIZE_MB }));
        return;
      }
      const storageRef = ref(storage, `attachments/${uuidv4()}`);
      await uploadBytes(storageRef, attachment);
      attachmentUrl = await getDownloadURL(storageRef);
      attachmentType = attachment.type.split("/")[0];
      attachmentName = attachment.name;
      setAttachment(null);
      setAttachmentError("");
    }

    const user = auth.currentUser;
    let userName = t("anonymousUser");
    if (user) {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        userName = `${userDoc.data().firstName || ""} ${userDoc.data().lastName || ""}`.trim() || userName;
      }
    }

    const newCommentObject: Comment = {
      id: uuidv4(),
      userName,
      comment: newComment,
      timestamp: new Date().toISOString(),
      attachmentUrl,
      attachmentType,
      attachmentName,
    };
    setComments((prev) => [newCommentObject, ...prev]);
    setNewComment("");
    setIsNotesModalOpen(false);

    if (organizationId && user) {
      const commentsRef = collection(db, "organizations", organizationId, "songs", song.id, "comments");
      await setDoc(doc(commentsRef, newCommentObject.id), newCommentObject);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDeleteComment = async (commentId: string) => {
    const commentToDelete = comments.find((c) => c.id === commentId);
    if (commentToDelete?.attachmentUrl) {
      try {
        const attachmentRef = ref(storage, commentToDelete.attachmentUrl);
        await deleteObject(attachmentRef);
      } catch (err) {
        console.error("Error deleting attachment:", err);
      }
    }
    setComments((prev) => prev.filter((c) => c.id !== commentId));
    if (organizationId && song?.id) {
      const commentsRef = collection(db, "organizations", organizationId, "songs", song.id, "comments");
      await deleteDoc(doc(commentsRef, commentId));
    }
  };

  const songId = initialSong?.id;

  useEffect(() => {
    if (!songId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const songDoc = await getDoc(doc(db, "songs", songId));
        if (songDoc.exists()) {
          const data = songDoc.data();
          setSong({ id: songDoc.id, ...data });
          const versions = data.chordVersions;
          const chordContent =
            versions && Array.isArray(versions) && versions.length > 0
              ? versions[0]?.content || ""
              : data.chords || data.charts || "";
          setChords(chordContent);
          setSavedChords(chordContent);
          setChordsEditing(false);
          setTransposeSemitones(0);
        } else {
          setSong(initialSong);
          const fallbackChords = initialSong?.chords || initialSong?.charts || "";
          setChords(fallbackChords);
          setSavedChords(fallbackChords);
        }

        let orgId = propOrgId;
        if (!orgId && auth.currentUser) {
          const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
          if (userDoc.exists()) orgId = userDoc.data().organizationId;
        }
        setOrganizationId(orgId || null);

        if (orgId) {
          const commentsRef = collection(db, "organizations", orgId, "songs", songId, "comments");
          const commentsSnapshot = await getDocs(commentsRef);
          const commentsData = commentsSnapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          })) as Comment[];
          setComments(commentsData);
        }
      } catch (err) {
        console.error("Error fetching song:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialSong used for fallback when doc doesn't exist; songId is the primary trigger
  }, [songId, propOrgId]);

  if (!song) return null;

  const songYoutubeLinks = getYoutubeLinksFromSong(song);

  return (
    <AnimatedModal
      isOpen
      onClose={onClose}
      backdropClassName="bg-black/60 backdrop-blur-sm"
      contentClassName="w-full sm:max-w-5xl lg:max-w-6xl"
    >
      <div className={modalPanelClass}>
          <div className="flex items-center justify-between px-6 py-4 md:px-8 md:py-5 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50/80 to-white dark:from-gray-800 dark:to-gray-800/80 shrink-0">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA] shrink-0">
              <HiOutlineMusicalNote className="w-6 h-6" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-gray-100 truncate">
                {song.title}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
                {song.artist}
                {song.album && ` · ${song.album}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shrink-0"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <span className="w-10 h-10 border-2 border-[#5932EA]/30 border-t-[#5932EA] rounded-full animate-spin" />
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left column */}
              <div className="flex-1 min-w-0 space-y-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <div className="shrink-0 self-center sm:self-auto">
                    <SongArtworkBlock
                      artworkURL={song.artworkURL}
                      title={song.title}
                      t={t}
                    />
                  </div>
                  <div className="sm:ml-4 flex-1 min-w-0 overflow-hidden">
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 line-clamp-2 leading-tight">
                      {song.title}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-2 truncate" title={song.artist}>
                      <span className="font-medium text-gray-500 dark:text-gray-500">{t("artist")}:</span>{" "}
                      {song.artist}
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 mt-0.5 truncate" title={song.album}>
                      <span className="font-medium text-gray-500 dark:text-gray-500">{t("album")}:</span>{" "}
                      {song.album}
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <div className="w-1 h-5 rounded-full bg-gradient-to-b from-[#5932EA] to-[#7C3AED]" />
                      <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                        {t("chords") || "Chords"}
                      </h2>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {chords && !chordsEditing && (
                        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg px-2 py-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400">{t("transpose")}:</span>
                          <button
                            type="button"
                            onClick={() => handleTranspose(-1)}
                            disabled={transposeSemitones <= -11}
                            className="w-7 h-7 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 disabled:opacity-40 text-sm font-medium"
                          >
                            −
                          </button>
                          <span className="min-w-[2rem] text-center text-sm font-semibold text-gray-800 dark:text-gray-200">
                            {transposeSemitones >= 0 ? `+${transposeSemitones}` : transposeSemitones}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleTranspose(1)}
                            disabled={transposeSemitones >= 11}
                            className="w-7 h-7 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 disabled:opacity-40 text-sm font-medium"
                          >
                            +
                          </button>
                        </div>
                      )}
                      {!chordsEditing && (
                        <button
                          type="button"
                          onClick={startChordsEdit}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            chords
                              ? "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-[#5932EA]/10 hover:text-[#5932EA] dark:hover:bg-[#5932EA]/20 dark:hover:text-[#5932EA]"
                              : "bg-[#5932EA] text-white hover:bg-[#4a28c7]"
                          }`}
                        >
                          <HiOutlinePencilSquare className="w-4 h-4" />
                          {chords ? (t("edit") || "Edit") : (t("addChords") || "Add chords")}
                        </button>
                      )}
                    </div>
                  </div>
                  {chordsEditing ? (
                    <div className="p-6 space-y-4">
                      <textarea
                        value={chords}
                        onChange={(e) => setChords(e.target.value)}
                        rows={12}
                        placeholder={t("chordsPlaceholder") || "Paste or type chord chart..."}
                        className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl font-mono text-sm text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-[#5932EA] resize-y min-h-[200px]"
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={handleSaveChords}
                          disabled={chordsSaving}
                          className="px-4 py-2 bg-[#5932EA] text-white text-sm font-medium rounded-lg hover:bg-[#4a28c7] transition-colors disabled:opacity-60"
                        >
                          {chordsSaving ? (t("loading") || "Saving...") : (t("save") || "Save")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setChords(savedChords);
                            setChordsEditing(false);
                            setTransposeSemitones(0);
                          }}
                          disabled={chordsSaving}
                          className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-60"
                        >
                          {t("cancel") || "Cancel"}
                        </button>
                      </div>
                    </div>
                  ) : chords ? (
                    <div className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 p-6 max-h-80 md:max-h-[28rem] overflow-y-auto">
                      <ChordChartWithHover
                        text={transposeChords(chords, transposeSemitones)}
                        className="text-gray-700 dark:text-gray-300"
                        chordClassName="text-[#5932EA] dark:text-[#7c5cf5]"
                      />
                    </div>
                  ) : (
                    <div className="text-center py-10 px-6">
                      <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                        {t("noChordsYet") || "No chords added yet."}
                      </p>
                      <button
                        type="button"
                        onClick={startChordsEdit}
                        className="px-4 py-2 bg-[#5932EA] text-white text-sm font-medium rounded-lg hover:bg-[#4a28c7] transition-colors"
                      >
                        {t("addChords") || "Add chords"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Right column */}
              <div className="flex-1 min-w-0 space-y-6">
                {song.id && (
                  <YoutubeVideosSection
                    songId={song.id}
                    initialLinks={songYoutubeLinks}
                    headerPadding="px-6 py-4"
                    bodyPadding="p-6"
                    showTransposeHint={false}
                    onSaved={(links) =>
                      setSong((prev: any) =>
                        prev
                          ? {
                              ...prev,
                              youtubeLinks: links,
                              youtubeLink: links[0] || "",
                            }
                          : null
                      )
                    }
                  />
                )}

                <div className="rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="w-1 h-5 rounded-full bg-gradient-to-b from-[#5932EA] to-[#7C3AED]" />
                      <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                        {t("notes")}
                      </h2>
                    </div>
                    <button
                      onClick={() => setIsNotesModalOpen(true)}
                      className="flex items-center gap-1.5 bg-[#5932EA] text-white text-sm px-3 py-2 rounded-xl hover:bg-[#4a28c7] transition-colors font-medium"
                    >
                      <HiOutlinePencilSquare className="w-4 h-4" strokeWidth={2} />
                      {t("addNote") || "Add note"}
                    </button>
                  </div>
                  <div className="p-6 max-h-64 md:max-h-80 overflow-y-auto space-y-2">
                    {comments.length > 0 ? (
                      comments.map((comment) => (
                        <li
                          key={comment.id}
                          className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 border border-gray-100 dark:border-gray-600 list-none"
                        >
                          {isYouTubeUrl(comment.comment) ? (
                            <iframe
                              width="100%"
                              height="140"
                              src={getYouTubeEmbedUrl(comment.comment) || ""}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              title="YouTube"
                              className="rounded-lg"
                            />
                          ) : isValidUrl(comment.comment) ? (
                            <a
                              href={comment.comment}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#5932EA] hover:text-[#4a28c7] break-all text-sm"
                            >
                              {comment.comment}
                            </a>
                          ) : (
                            <p className="text-gray-700 dark:text-gray-300 text-sm">{comment.comment}</p>
                          )}
                          {comment.attachmentUrl && (
                            <div className="mt-2">
                              {comment.attachmentType === "image" ? (
                                <img
                                  src={comment.attachmentUrl}
                                  alt={t("attachment")}
                                  className="max-w-full h-auto rounded-lg max-h-24 object-contain"
                                />
                              ) : (
                                <a
                                  href={comment.attachmentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#5932EA] hover:text-[#4a28c7] text-sm"
                                >
                                  {comment.attachmentName || t("attachment")}
                                </a>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-600 dark:text-gray-300">{comment.userName}</span>
                            <span>{new Date(comment.timestamp).toLocaleString()}</span>
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="ml-auto text-red-500 hover:text-red-600"
                            >
                              {t("delete")}
                            </button>
                          </div>
                        </li>
                      ))
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-6">
                        {t("noNotesYet") || "No notes yet."}
                      </p>
                    )}
                  </div>
                </div>

                <div className="rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
                    <div className="w-1 h-5 rounded-full bg-gradient-to-b from-[#5932EA] to-[#7C3AED]" />
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                      {t("metronome")}
                    </h2>
                  </div>
                  <div className="p-6 flex items-center gap-4">
                    <label className="text-sm text-gray-600 dark:text-gray-400">{t("bpm")}:</label>
                    <input
                      type="number"
                      value={bpm}
                      onChange={(e) => setBpm(Math.min(240, Math.max(40, parseInt(e.target.value) || 120)))}
                      min={40}
                      max={240}
                      className="border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-2 w-20 text-center bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#5932EA]/30"
                    />
                    <button
                      onClick={toggleMetronome}
                      className="px-4 py-2 rounded-xl bg-[#5932EA] text-white text-sm font-medium hover:bg-[#4a28c7] transition-colors"
                    >
                      {isPlaying ? t("stop") : t("start")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Note Modal */}
      <AnimatedModal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        backdropClassName="bg-black/60 backdrop-blur-sm"
      >
        <div className="relative w-full max-w-md rounded-2xl shadow-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50/80 to-white dark:from-gray-800 dark:to-gray-800/80">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA] shadow-sm">
                <HiOutlinePencilSquare className="w-6 h-6" strokeWidth={2} />
              </div>
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
                {t("addNote") || "Add note"}
              </h2>
            </div>
            <button
              onClick={() => setIsNotesModalOpen(false)}
              className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label={t("close")}
            >
              <HiOutlineXMark className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6 space-y-4">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={t("newNotes") || "Write a note..."}
              rows={4}
              maxLength={400}
              className="w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] resize-none transition-all"
            />
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400 cursor-pointer hover:border-[#5932EA]/50 hover:text-[#5932EA] transition-colors">
                <HiOutlinePaperClip className="w-4 h-4" />
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*, audio/*, application/pdf, application/msword, .docx, .ppt, .pptx"
                  onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                  className="hidden"
                />
                {t("attachment") || "Attach"}
              </label>
              {attachmentError && <p className="text-red-500 text-xs">{attachmentError}</p>}
            </div>
          </div>
          <div className="flex gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
            <button
              onClick={() => setIsNotesModalOpen(false)}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium"
            >
              {t("cancel") || "Cancel"}
            </button>
            <button
              onClick={handleAddComment}
              disabled={!newComment.trim() && !attachment}
              className="flex-1 bg-[#5932EA] text-white text-sm px-4 py-2.5 rounded-xl hover:bg-[#4a28c7] disabled:opacity-50 transition-colors font-medium"
            >
              {t("addNotes") || "Add"}
            </button>
          </div>
        </div>
      </AnimatedModal>
    </AnimatedModal>
  );
};

export default SongDetailModal;
